import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, GraduationCap, Sparkles, Video, Baby, Stethoscope, Calculator } from "lucide-react";

const RESOURCE_HERO_CATEGORIES = [
  { key: "courses",    label: "Courses",    icon: GraduationCap, accent: "from-violet-500",  haloBg: "bg-violet-100",  iconColor: "text-violet-500",  body: "Expert-led, bite-sized lessons for parents and therapists." },
  { key: "books",      label: "Books",      icon: BookOpen,      accent: "from-coral",       haloBg: "bg-coral/10",    iconColor: "text-coral",       body: "Hand-picked reading for every developmental stage." },
  { key: "activities", label: "Activities", icon: Sparkles,      accent: "from-emerald-500", haloBg: "bg-emerald-100", iconColor: "text-emerald-500", body: "Practical home activities, organised by skill area." },
  { key: "videos",     label: "Videos",     icon: Video,         accent: "from-amber-500",   haloBg: "bg-amber-100",   iconColor: "text-amber-500",   body: "Short demonstrations you can watch and try the same day." },
];

const FREE_TOOLS = [
  { to: "/milestones",            testid: "milestones",   icon: Baby,        haloBg: "bg-coral/10",   iconColor: "text-coral",      label: "Development Milestones",     body: "What most children learn at each age — 1 to 12 years." },
  { to: "/free-screening",        testid: "free-screening", icon: Stethoscope, haloBg: "bg-mint/15",  iconColor: "text-mint",       label: "Free Child Screening",       body: "5-minute therapist-reviewed developmental screening." },
  { to: "/child-age-calculator",  testid: "child-age",    icon: Calculator,  haloBg: "bg-amber-100",  iconColor: "text-amber-600",  label: "Child Age Calculator",       body: "Exact age in years, months, days + development stage." },
];

/** Resources hub teaser — 4 category tiles + "Show all" link to /resources. */
export default function ResourcesSection() {
  return (
    <section className="relative bg-cream kpp-blend-top-white py-16 lg:py-20 overflow-hidden" id="resources-preview">
      <div aria-hidden="true" className="pointer-events-none absolute -top-12 left-1/4 w-56 h-56 bg-violet-100 rounded-full blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 -right-12 w-56 h-56 bg-mint/15 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14 kpp-reveal" data-reveal>
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-violet-100 text-violet-700 rounded-full text-[11px] font-bold uppercase tracking-widest mb-5">
            <BookOpen className="w-3.5 h-3.5" /> Resources Hub
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
            A library of support, <span className="text-coral block sm:inline">always at your fingertips.</span>
          </h2>
          <p className="mt-5 text-inkmid text-sm sm:text-base leading-relaxed">
            Curated by therapists. Free to browse. Pick a category to get started.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 kpp-reveal" data-reveal data-stagger>
          {RESOURCE_HERO_CATEGORIES.map((c, i) => (
            <Link
              key={c.key}
              style={{ "--i": i }}
              to={`/resources?category=${c.key}`}
              className="relative bg-white rounded-2xl shadow-soft border border-plum/5 px-3 py-5 sm:py-6 flex flex-col items-center text-center hover-lift overflow-hidden"
              data-testid={`resource-tile-${c.key}`}
            >
              <span className={`absolute top-0 left-4 right-4 h-1 rounded-b-full bg-gradient-to-r ${c.accent} to-transparent`} />
              <div className={`w-14 h-14 rounded-full ${c.haloBg} flex items-center justify-center mb-3 mt-1`}>
                <c.icon className={`w-6 h-6 ${c.iconColor}`} strokeWidth={2.2} />
              </div>
              <p className="font-heading text-sm sm:text-base leading-tight text-inkdark">{c.label}</p>
              <p className="hidden sm:block text-[11px] text-inkmid leading-snug mt-1.5">{c.body}</p>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-3">
          <Link
            to="/resources"
            data-testid="resources-show-all-btn"
            className="inline-flex items-center gap-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-full px-6 py-2.5 text-sm font-bold transition-colors shadow-soft"
          >
            Show all categories <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Free tools strip — surfaces the 3 high-intent SEO landings */}
        <div className="mt-12 sm:mt-14 pt-8 sm:pt-10 border-t border-plum/10">
          <div className="text-center mb-6 sm:mb-8 kpp-reveal" data-reveal>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-mint/15 text-mint rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> Free Tools
            </span>
            <h3 className="font-heading text-2xl sm:text-3xl text-inkdark">
              Try one of our <span className="text-coral">free tools</span> — no sign-up.
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 kpp-reveal" data-reveal data-stagger>
            {FREE_TOOLS.map((t, i) => (
              <Link
                key={t.to}
                to={t.to}
                style={{ "--i": i }}
                className="group relative bg-white rounded-2xl shadow-soft border border-plum/5 p-5 sm:p-6 flex items-start gap-4 hover-lift overflow-hidden"
                data-testid={`free-tool-tile-${t.testid}`}
              >
                <span className={`shrink-0 w-12 h-12 rounded-xl ${t.haloBg} flex items-center justify-center`}>
                  <t.icon className={`w-6 h-6 ${t.iconColor}`} strokeWidth={2.2} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-base text-inkdark group-hover:text-coral transition-colors">{t.label}</p>
                  <p className="text-xs text-inkmid leading-snug mt-1">{t.body}</p>
                </div>
                <ArrowRight className="shrink-0 w-4 h-4 text-inkmid group-hover:text-coral group-hover:translate-x-0.5 transition" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
